import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Plus, Edit, Check, Minus, Power, PowerOff } from 'lucide-react';
import { toast } from 'react-hot-toast';
import DataTable from '../DataTable';
import DepartmentModal from './DepartmentModal';
import ConfirmDialog from '../ConfirmDialog';
import IconButton from '../common/IconButton';
import SmartSearchBar from '../leads/SmartSearchBar';
import { DepartmentDto, CreateDepartmentDto, UpdateDepartmentDto, DepartmentUsageDto, departmentsApi } from '../../services/settingsService';
import { handleApiError } from '../../utils/errorHandler';

const TabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchWrapper = styled.div`
  flex: 1;
  max-width: 400px;

  @media (max-width: 767px) {
    max-width: 100%;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover { background: var(--hover-background); }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CenteredCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const TextCell = styled.span`
  font-size: 14px;
  color: var(--text-primary);
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
`;

const DepartmentsTab: React.FC = () => {
  const [departments, setDepartments] = useState<DepartmentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<DepartmentDto | null>(null);
  const [toggleConfirmOpen, setToggleConfirmOpen] = useState(false);
  const [departmentToToggle, setDepartmentToToggle] = useState<DepartmentDto | null>(null);
  const [toggleUsage, setToggleUsage] = useState<DepartmentUsageDto | null>(null);

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      setLoading(true);
      const data = await departmentsApi.getAll();
      setDepartments(data);
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בטעינת מחלקות'));
    } finally {
      setLoading(false);
    }
  };

  // סינון מקומי לפי שם (הרשימה קצרה — כל המחלקות נטענות מראש)
  const filteredDepartments = useMemo(() => {
    if (!searchTerm) return departments;
    const search = searchTerm.toLowerCase();
    return departments.filter(dep => dep.name.toLowerCase().includes(search));
  }, [departments, searchTerm]);

  const handleAddDepartment = () => {
    setSelectedDepartment(null);
    setModalOpen(true);
  };

  const handleEditDepartment = (department: DepartmentDto) => {
    setSelectedDepartment(department);
    setModalOpen(true);
  };

  const handleToggleActive = async (department: DepartmentDto) => {
    setDepartmentToToggle(department);
    setToggleUsage(null);
    setToggleConfirmOpen(true);
    // בהשבתה בלבד — שליפת ספירת התיקים המשויכים כדי להזהיר (לא חוסם)
    if (department.isActive) {
      try {
        const usage = await departmentsApi.getUsage(department.id);
        setToggleUsage(usage);
      } catch {
        setToggleUsage(null);
      }
    }
  };

  const handleSaveDepartment = async (departmentData: CreateDepartmentDto | UpdateDepartmentDto) => {
    try {
      if (selectedDepartment) {
        await departmentsApi.update(selectedDepartment.id, departmentData as UpdateDepartmentDto);
      } else {
        await departmentsApi.create(departmentData as CreateDepartmentDto);
      }
      await loadDepartments();
    } catch (error) {
      console.error('שגיאה בשמירת מחלקה:', error);
      throw error;
    }
  };

  const confirmToggle = async () => {
    if (!departmentToToggle) return;

    const nextActive = !departmentToToggle.isActive;
    try {
      await departmentsApi.update(departmentToToggle.id, {
        name: departmentToToggle.name,
        isActive: nextActive
      });
      await loadDepartments();
      toast.success(nextActive ? 'המחלקה הופעלה' : 'המחלקה הושבתה');
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בעדכון סטטוס המחלקה'));
    } finally {
      setToggleConfirmOpen(false);
      setDepartmentToToggle(null);
      setToggleUsage(null);
    }
  };

  // הודעת האזהרה בהשבתה — כוללת ספירת תיקים מושפעים (אם קיימים)
  const buildToggleMessage = (): string => {
    if (!departmentToToggle) return '';
    if (!departmentToToggle.isActive) {
      return `האם להפעיל מחדש את המחלקה "${departmentToToggle.name}"?`;
    }
    const affected = (toggleUsage?.leadsCount ?? 0) + (toggleUsage?.clientsCount ?? 0);
    if (affected > 0) {
      return `שים לב: למחלקה "${departmentToToggle.name}" משויכים ${toggleUsage!.leadsCount} לידים ו-${toggleUsage!.clientsCount} לקוחות. ` +
        `לאחר ההשבתה היא לא תופיע בפילטר הרשימות — מומלץ לשייך אותם מחדש קודם. התיקים עצמם יישמרו. להשבית בכל זאת?`;
    }
    return `האם להשבית את המחלקה "${departmentToToggle.name}"? היא לא תופיע בבוררי-שיוך ובפילטר, אך תיקים שכבר משויכים אליה יישמרו.`;
  };

  const columns = [
    {
      key: 'name',
      title: 'שם המחלקה',
      width: '260px',
      render: (value: string) => (
        <CenteredCell>
          <TextCell>{value}</TextCell>
        </CenteredCell>
      )
    },
    {
      key: 'isActive',
      title: 'סטטוס',
      width: '100px',
      render: (value: boolean) => (
        <CenteredCell>
          {value ? (
            <Check size={14} color="#16a34a" strokeWidth={2.5} />
          ) : (
            <Minus size={14} color="#d1d5db" />
          )}
        </CenteredCell>
      )
    },
    {
      key: 'actions',
      title: 'פעולות',
      width: '110px',
      render: (_: any, department: DepartmentDto) => (
        <ActionsCell>
          <IconButton
            icon={Edit}
            onClick={() => handleEditDepartment(department)}
            title="עריכה"
          />
          <IconButton
            icon={department.isActive ? PowerOff : Power}
            onClick={() => handleToggleActive(department)}
            title={department.isActive ? 'השבתה' : 'הפעלה'}
            variant={department.isActive ? 'danger' : undefined}
          />
        </ActionsCell>
      )
    }
  ];

  return (
    <>
      <TabHeader>
        <SearchWrapper>
          <SmartSearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="חיפוש מחלקות..."
          />
        </SearchWrapper>
        <Button variant="primary" onClick={handleAddDepartment}>
          <Plus size={16} />
          מחלקה חדשה
        </Button>
      </TabHeader>

      <DataTable
        data={filteredDepartments}
        columns={columns}
        loading={loading}
      />

      <DepartmentModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveDepartment}
        department={selectedDepartment}
        title={selectedDepartment ? 'עריכת מחלקה' : 'הוספת מחלקה חדשה'}
      />

      <ConfirmDialog
        isOpen={toggleConfirmOpen}
        onClose={() => { setToggleConfirmOpen(false); setToggleUsage(null); }}
        onConfirm={confirmToggle}
        title={departmentToToggle?.isActive ? 'השבתת מחלקה' : 'הפעלת מחלקה'}
        message={buildToggleMessage()}
        type={departmentToToggle?.isActive ? 'danger' : 'info'}
      />
    </>
  );
};

export default DepartmentsTab;
